/**
 * R5: Slug typo detection [FLAG ONLY, no auto-fix]
 *
 * Heurystyki wykrywające realne błędy w slugach. Stopwords PL+EN zwolnione z
 * "short_middle_token" — zwykłe spójniki ("w", "do", "to", "i", "za") NIE są błędem,
 * tylko normalną częścią slugu w języku naturalnym.
 *
 *  1. double_hyphen — "foo--bar" (tokenization artefact)
 *  2. short_middle_token — token ≤ 2 znaki w środku, który NIE jest stopword
 *  3. repeated_token — powtórzony consecutive ("foo-foo-bar")
 *  4. too_long — slug > 80 znaków (LLM przepisało pół tytułu)
 *  5. non_ascii — slug nie został slugifikowany (polskie ą/ć/ę/ł/ń/ó/ś/ż/ź)
 *
 * Output: FlaggedSlug[] — user widzi listę, decyduje co zrobić (manual).
 * Zero writes do DB, zero rollback payload.
 */

import type {
  RepairRuleInput,
  RepairRuleResult,
  FlaggedSlug,
} from "@/types/mapRepair";

const MAX_SLUG_LENGTH = 80;
const MIN_MIDDLE_TOKEN_LENGTH = 3;

/**
 * Krótkie słowa PL + EN które są NORMALNE w slugach — spójniki / przyimki /
 * zaimki / czasowniki pomocnicze. NIE są błędem nawet gdy długość <= 2.
 * Lista pokrywa oba języki dla międzynarodowych map.
 */
const SHORT_STOPWORDS = new Set<string>([
  // Polski — przyimki i spójniki
  "i", "a", "o", "u", "w", "z", "za", "na", "do", "to", "po", "od", "ze",
  "bo", "by", "co", "sa", "ma", "ja", "my", "mi", "ci", "go", "on", "ta",
  "te", "ten", "tej", "tym", "czy", "lub", "aby", "gdy", "nad", "pod", "dla",
  "bez", "ale", "nie", "juz", "tez", "tam", "tu", "nim", "sie", "sa",
  // English
  "an", "of", "in", "on", "at", "by", "or", "is", "as", "it", "be",
  "we", "he", "if", "so",
]);

export function detectSlugIssues(slug: string): string[] {
  const reasons: string[] = [];

  if (slug.includes("--")) {
    reasons.push("double_hyphen");
  }

  if (slug.length > MAX_SLUG_LENGTH) {
    reasons.push(`too_long_${slug.length}chars`);
  }

  // eslint-disable-next-line no-control-regex
  if (/[^\x00-\x7F]/.test(slug)) {
    reasons.push("non_ascii");
  }

  const tokens = slug.split("-");

  // Short middle token — wykluczamy pierwszy/ostatni + stopwords whitelist
  for (let i = 1; i < tokens.length - 1; i++) {
    const t = tokens[i];
    if (
      t.length < MIN_MIDDLE_TOKEN_LENGTH &&
      t.length > 0 &&
      !SHORT_STOPWORDS.has(t.toLowerCase())
    ) {
      reasons.push(`short_middle_token:${t}`);
      break; // jedna flaga wystarczy
    }
  }

  // Repeated consecutive token
  for (let i = 1; i < tokens.length; i++) {
    if (tokens[i] === tokens[i - 1] && tokens[i].length > 0) {
      reasons.push(`repeated_token:${tokens[i]}`);
      break;
    }
  }

  return reasons;
}

export function runR5(input: RepairRuleInput): RepairRuleResult {
  const flags: FlaggedSlug[] = [];

  for (const node of input.nodes) {
    const reasons = detectSlugIssues(node.slug);
    if (reasons.length > 0) {
      flags.push({
        node_id: node.id,
        slug: node.slug,
        reasons,
      });
    }
  }

  return {
    rule: "R5",
    operations: [], // zero writes
    proposals: [],
    flags,
    skipped: [],
  };
}
